import { FileUpload, type FileUploadController } from "@msuite/ui/file-upload";
import { Combobox, Select, Textarea, TextInput } from "@msuite/ui/form";
import { getFulfillmentTypeIcon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardNull } from "@msuite/utils/misc";
import { getFulfillmentTypeTranslationKey } from "@msuite/utils/organizations";
import type { ServiceFormSchema } from "@msuite/utils/schemas";
import { useFormContext, useWatch } from "react-hook-form";
import { FileCardRemote } from "@/components/files";
import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";

/** Props Interface */
interface ServiceFormProps {
	fileUploadController: FileUploadController;
}

export function ServiceForm({ fileUploadController }: ServiceFormProps) {
	/** Context */
	const methods = useFormContext<ServiceFormSchema>();
	const [currentId] = useWatch({
		control: methods.control,
		name: ["id"],
	});

	/** Hooks */
	const mentionOptions = useMentionOptions();
	const { t } = useTranslation();
	const { data } = useQuery({
		queryKey: ["services"],
		queryFn: async () => {
			const response = await client.organizations.services.$get();
			const data = await responseHandler(response);
			return data.services;
		},
	});

	/** Render */
	return (
		<div className="v-stack gap-4">
			<Combobox
				className="max-w-75"
				control={methods.control}
				name="parentServiceId"
				displayValue={(item) => item?.item.service.shortText || ""}
				keyExtractor={(item) => item?.value || ""}
				placeholder={t("placeholders.select_service")}
				isNullable
				isRequired={false}
				label={t("organization.data.parent_service")}
				data-cy="service-parent-service-combobox"
				onSelect={({ value }) => {
					methods.setValue("parentServiceId", guardNull(value));
					client.organizations.services["suggest-number"]
						.$get({
							query: { parentServiceId: value || undefined },
						})
						.then(async (response: any) => {
							const data = await responseHandler(response);
							methods.setValue("number", data.suggestedNumber);
						});
				}}
				options={(
					data?.map((service) => ({
						label: service.shortText,
						value: service.id,
						item: { service },
					})) || []
				).filter(
					(s) => s.value !== currentId && !s.item.service.ancestorIds?.includes(currentId || ""),
				)}
			/>
			<Select
				control={methods.control}
				name="fulfillmentType"
				label={t("organization.data.fulfillment_type")}
				itemNotFoundLabel={t("general.not_found")}
				isRequired
				isNullable={false}
				data-cy="service-fulfillment-type-select"
				renderOption={(_, __, value) => (
					<div className="h-stack">
						<Icon name={getFulfillmentTypeIcon(value)} />
						<p>{t(getFulfillmentTypeTranslationKey(value))}</p>
					</div>
				)}
				options={
					[
						{
							label: t("organization.data.internal_service"),
							value: "internal" as const,
							item: {},
						},
						{
							label: t("organization.data.external_service"),
							value: "external" as const,
							item: {},
						},
					] as const
				}
			/>
			<TextInput
				isRequired
				label={t("organization.data.atomic_number")}
				control={methods.control}
				name="number"
				type="number"
				min={0}
				step={1}
				data-cy="service-number-input"
				placeholder="1"
			/>
			<TextInput
				isRequired
				label={t("organization.data.short_text")}
				control={methods.control}
				name="shortText"
				placeholder={t("placeholders.short_text")}
				data-cy="service-short-text-input"
			/>
			<Textarea
				isRequired={false}
				label={t("organization.data.long_text")}
				control={methods.control}
				name="longText"
				placeholder={t("placeholders.long_text")}
				data-cy="service-long-text-input"
				{...mentionOptions}
			/>
			<FileUpload
				controller={fileUploadController}
				renderFile={(file) => (
					<FileCardRemote
						fileId={file.id}
						onInteraction={async () => {}}
					/>
				)}
			/>
		</div>
	);
}
